import { useState } from "react";
import type { ReasonPayload } from "@/services/reasonCodes";
import { ReasonCancelled, isReasonCancelled, useReasonDialog } from "@/shared/ui/ReasonDialog";   // REASON-1
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2, Plus, Calendar, Sparkles } from "lucide-react";
import { pricingApi } from "@/services/pricing";
import { Can } from "@/auth/permissions";
import { PageShell, Pill, LoadingState, EmptyState } from "@/shared/ui/PageHeader";

/**
 * W4 · 节假日加价配置
 *  - 日期范围 + 加价率（uplift_pct · 正数 = 涨价，负数 = 让利）
 *  - 列表 + 加 + 删
 *  - 进入公式: final = base × (1 + ch%) × (1 + holiday%) × (1 + customer%)
 */
export function HolidayPricingPage() {
  const qc = useQueryClient();
  const holidays = useQuery({
    queryKey: ["pricing", "holidays"],
    queryFn: () => pricingApi.listHolidays(),
  });

  const [name, setName] = useState("");
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [upliftPct, setUpliftPct] = useState(20);
  const [note, setNote] = useState("");

  const { ask: askReason, dialog: reasonDialog } = useReasonDialog();   // REASON-1
  const create = useMutation({
    mutationFn: async () => {
      const r = await askReason({
        scene: "price_change", title: "新增节假日加价", subtitle: name,
        changes: [{ label: `${dateStart} ~ ${dateEnd}`, before: "无加价", after: `${upliftPct > 0 ? "+" : ""}${upliftPct}%` }], confirmText: "确认并保存",
      });
      if (!r) throw new ReasonCancelled();
      return pricingApi.createHoliday({ name, date_start: dateStart, date_end: dateEnd, uplift_pct: upliftPct, note: note || undefined, ...r });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pricing", "holidays"] });
      setName("");
      setDateStart("");
      setDateEnd("");
      setUpliftPct(20);
      setNote("");
    },
    onError: (e) => { if (!isReasonCancelled(e)) alert((e as Error).message || "保存失败"); },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const r = await askReason({ scene: "price_change", required: false, title: "删除节假日加价", subtitle: holidays.data?.find((h) => h.id === id)?.name, danger: true, confirmText: "确认删除" });
      if (!r) throw new ReasonCancelled();
      return pricingApi.deleteHoliday(id, r);
    },
    onError: (e) => { if (!isReasonCancelled(e)) alert((e as Error).message || "删除失败"); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pricing", "holidays"] }),
  });

  const canCreate = name && dateStart && dateEnd;

  return (
    <PageShell
      title="节假日加价"
      subtitle="日期范围内自动应用 uplift · 进入定价公式的第 3 因子"
    >
      <Can perm="pricing.holidays.manage">
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="mb-4 flex items-center gap-2">
          <Calendar className="h-4 w-4 text-amber-500" />
          <h2 className="text-base font-semibold text-slate-900">新建节假日加价</h2>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
          <Field label="名称" span={2}>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="如：圣诞新年 / 国庆黄金周"
              className="h-9 w-full rounded-lg border border-slate-200 px-3 text-[13px]"
            />
          </Field>
          <Field label="开始日">
            <input
              type="date"
              value={dateStart}
              onChange={(e) => setDateStart(e.target.value)}
              className="h-9 w-full rounded-lg border border-slate-200 px-3 text-[13px]"
            />
          </Field>
          <Field label="结束日">
            <input
              type="date"
              value={dateEnd}
              onChange={(e) => setDateEnd(e.target.value)}
              className="h-9 w-full rounded-lg border border-slate-200 px-3 text-[13px]"
            />
          </Field>
          <Field label="加价 %">
            <input
              type="number"
              step="0.5"
              value={upliftPct}
              onChange={(e) => setUpliftPct(Number(e.target.value))}
              className="h-9 w-full rounded-lg border border-slate-200 px-3 text-[13px]"
            />
          </Field>
        </div>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="md:col-span-3">
            <Field label="备注">
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="h-9 w-full rounded-lg border border-slate-200 px-3 text-[13px]"
              />
            </Field>
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={() => create.mutate()}
              disabled={!canCreate || create.isPending}
              className="flex h-9 w-full items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-4 text-[13px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              <Plus className="h-3.5 w-3.5" /> 添加
            </button>
          </div>
        </div>

        {create.isError && (
          <div className="mt-2 text-[12px] text-rose-600">
            创建失败：{(create.error as Error).message}
          </div>
        )}
      </div>
      </Can>

      {holidays.isLoading ? (
        <LoadingState />
      ) : (holidays.data ?? []).length === 0 ? (
        <EmptyState message="暂无节假日配置 · 上方新建" />
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">名称</th>
                <th className="px-4 py-3 text-left">日期区间</th>
                <th className="px-4 py-3 text-right">加价</th>
                <th className="px-4 py-3 text-left">备注</th>
                <th className="px-4 py-3 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {(holidays.data ?? []).map((h) => (
                <tr key={h.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-semibold text-slate-900">
                    <Sparkles className="mr-1 inline h-3.5 w-3.5 text-amber-500" />
                    {h.name}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {h.date_start} → {h.date_end}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Pill tone={h.uplift_pct >= 0 ? "warning" : "info"}>
                      {h.uplift_pct > 0 ? "+" : ""}
                      {h.uplift_pct}%
                    </Pill>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{h.note ?? "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <Can perm="pricing.holidays.manage"><button
                      type="button"
                      onClick={() => remove.mutate(h.id)}
                      className="flex h-7 items-center gap-1 rounded-md border border-rose-200 bg-rose-50 px-2 text-[11px] font-semibold text-rose-700 hover:bg-rose-100"
                    >
                      <Trash2 className="h-3 w-3" /> 删除
                    </button></Can>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    {reasonDialog}
    </PageShell>
  );
}

function Field({
  label,
  children,
  span,
}: {
  label: string;
  children: React.ReactNode;
  span?: 2 | 3 | 4;
}) {
  const spanCls = span === 2 ? "md:col-span-2" : span === 3 ? "md:col-span-3" : span === 4 ? "md:col-span-4" : "";
  return (
    <label className={"flex flex-col gap-1 " + spanCls}>
      <span className="text-[11px] font-medium text-slate-600">{label}</span>
      {children}
    </label>
  );
}
